import React, {
  FC,
  createContext,
  PropsWithChildren,
  useState,
  useContext,
  useCallback,
} from "react";
import {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
  useQuery,
} from "react-query";
import { User } from "../@types";
import { APIResponse, API_URL, get } from "../api";
import IdleSessionWarning, { SESSION_ACTIVITY_KEY } from "../components/IdleSessionWarning";
import { TRIP_BOOKING_ID_KEY } from "./TripsContext";

export const Auth = createContext<{
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  logout: () => void;
  isFetchingUser: boolean;
  isRefetching: boolean;
  refetch: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<APIResponse<any>, any>> | Promise<null>;
}>({
  user: null,
  isFetchingUser: false,
  isRefetching: false,
  async refetch() {
    //do nothing
    return null;
  },
  setUser() {
    // do nothing
  },
  logout() {
    // do nothing
  },
});

export const useAuth = () => {
  const context = useContext(Auth);

  return context;
};

const AuthContext: FC<PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);

  const { isLoading, isRefetching, refetch } = useQuery(
    "profile",
    async () => {
      const token = localStorage.getItem("token");

      return await get(API_URL + "/account/user/account/profile", {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      enabled:
        !!localStorage.getItem("token") &&
        !(typeof window !== "undefined" && window.location.pathname.startsWith("/resume-onboarding")),
      refetchOnWindowFocus: false,
      refetchInterval: false,
      refetchOnMount: false,
      onSuccess(data) {
        setUser(data.data);
      },
    }
  );

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem(SESSION_ACTIVITY_KEY);
    localStorage.removeItem(TRIP_BOOKING_ID_KEY);
    setUser(null);
    window.location.replace("/login");
  }, []);

  return (
    <Auth.Provider
      value={{
        user,
        setUser,
        logout,
        isFetchingUser: isLoading || isRefetching,
        isRefetching,
        refetch,
      }}
    >
      {children}
      {!!user?.password_set && <IdleSessionWarning userId={String(user.id)} logout={logout} />}
    </Auth.Provider>
  );
};

export default AuthContext;
